/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Skeleton } from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { Link } from '@tanstack/react-router'
import { useAuth } from '@web/src/hooks/use-auth'
import { useTranslation } from '@web/src/lib/intl/react'
import type { AdminProfilePaymentsCountsQuery, AdminProfilePaymentsCountsQueryVariables } from 'types/graphql'

import { formatPaymentUsd } from './admin-payments-summary'

const ADMIN_PROFILE_PAYMENTS_COUNTS_QUERY: TypedDocumentNode<
  AdminProfilePaymentsCountsQuery,
  AdminProfilePaymentsCountsQueryVariables
> = gql`
  query AdminProfilePaymentsCountsQuery($profileId: String!) {
    adminProfilePaymentsCounts(profileId: $profileId) {
      pendingCount
      approvedCount
      refundedCount
      supportedTotal
    }
  }
`

function useProfilePaymentsCounts(profileId: string) {
  const { hasPermission } = useAuth()
  const allowed = hasPermission('admin:payment-requests')
  const { data, error, networkStatus } = useQuery(ADMIN_PROFILE_PAYMENTS_COUNTS_QUERY, {
    skip: !allowed,
    variables: { profileId },
  })

  return {
    allowed,
    counts: data?.adminProfilePaymentsCounts,
    error,
    isInitialLoading: networkStatus === NetworkStatus.loading,
  }
}

type AdminProfilePaymentsProps = {
  profileId: string
}

export function AdminProfilePayments({ profileId }: AdminProfilePaymentsProps) {
  const { t } = useTranslation()
  const { allowed, counts, error, isInitialLoading } = useProfilePaymentsCounts(profileId)

  if (!allowed) {
    return null
  }

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="text-base">{t('Payments')}</CardTitle>
        <CardDescription>{t('Approved payments that were not refunded, plus request counts.')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {error ? <p className="text-destructive">{error.message}</p> : null}
        {isInitialLoading ? <Skeleton className="h-8 w-full" /> : null}
        {counts ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <p className="text-muted-foreground">{t('Total supported')}</p>
              <p className="font-medium text-lg tabular-nums">{formatPaymentUsd(counts.supportedTotal)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t('Pending')}</p>
              <p className="font-medium text-lg tabular-nums">{counts.pendingCount}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t('Approved')}</p>
              <p className="font-medium text-lg tabular-nums">{counts.approvedCount}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t('Refunded')}</p>
              <p className="font-medium text-lg tabular-nums">{counts.refundedCount}</p>
            </div>
          </div>
        ) : null}
        <Link
          className="inline-block font-medium text-sm underline"
          search={{ profileId, tab: 'approved' } as never}
          to="/admin/payments"
        >
          {t('View payments')}
        </Link>
      </CardContent>
    </Card>
  )
}

type AdminProfileSupportStatProps = {
  profileId: string
  className?: string
}

export function AdminProfileSupportStat({ profileId, className }: AdminProfileSupportStatProps) {
  const { t } = useTranslation()
  const { allowed, counts, isInitialLoading } = useProfilePaymentsCounts(profileId)

  if (!allowed) {
    return null
  }

  return (
    <Card className={cn('overflow-hidden shadow-sm', className)}>
      <CardHeader className="space-y-1 pb-2">
        <CardDescription className="text-xs uppercase tracking-wide">{t('Total supported')}</CardDescription>
        <CardTitle className="font-medium text-base leading-snug">
          {isInitialLoading || !counts ? (
            <Skeleton className="h-6 w-20" />
          ) : (
            <span className="tabular-nums">{formatPaymentUsd(counts.supportedTotal)}</span>
          )}
          <Link
            className="mt-2 block font-normal text-sm underline"
            search={{ profileId, tab: 'approved' } as never}
            to="/admin/payments"
          >
            {t('View payments')}
          </Link>
        </CardTitle>
      </CardHeader>
    </Card>
  )
}
